import { Package, Search, Filter, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';

function EmptyState({ type = 'default', searchTerm, hasActiveFilters, onClearFilters }) {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const states = {
    search: {
      icon: Search,
      title: t('noSearchResults'),
      message: t('noSearchResultsMessage'),
      suggestions: [
        t('searchSuggestion1'),
        t('searchSuggestion2'),
        t('searchSuggestion3')
      ],
      cta: {
        text: t('backToHome'),
        to: '/'
      }
    },
    category: {
      icon: Package,
      title: t('noCategoryProducts'),
      message: t('noCategoryProductsMessage'),
      suggestions: [
        t('categorySuggestion1'),
        t('categorySuggestion2')
      ],
      cta: {
        text: t('browseAllCategories'),
        to: '/categories'
      }
    },
    filtered: {
      icon: Filter,
      title: t('noFilteredResults'),
      message: t('noFilteredResultsMessage'),
      suggestions: [
        t('filterSuggestion1'),
        t('filterSuggestion2')
      ],
      showClearFilters: true
    },
    default: {
      icon: Package,
      title: t('noProductsAvailable'),
      message: t('noProductsAvailableMessage'),
      suggestions: [],
      cta: {
        text: t('backToHome'),
        to: '/'
      }
    }
  };

  const state = states[type] || states.default;
  const Icon = state.icon;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      {/* Icon */}
      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
        <Icon className="w-10 h-10 text-gray-500" aria-hidden="true" />
      </div>

      {/* Title */}
      <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
        {state.title}
      </h3>

      {/* Message */}
      <p className="text-gray-600 mb-6 text-center max-w-md">
        {searchTerm ? `${state.message} "${searchTerm}"` : state.message}
      </p>

      {/* Suggestions */}
      {state.suggestions.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-4 mb-6 max-w-md w-full">
          <p className="font-medium text-blue-900 mb-2">{t('suggestions')}:</p>
          <ul className="space-y-1">
            {state.suggestions.map((suggestion, index) => (
              <li key={index} className="text-sm text-blue-800 flex items-start">
                <span className="mr-2">•</span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        {state.showClearFilters && hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="btn-primary flex items-center space-x-2"
          >
            <Filter className="w-4 h-4" aria-hidden="true" />
            <span>{t('clearFilters')}</span>
          </button>
        )}

        {state.cta && (
          <Link to={state.cta.to} className="btn-secondary flex items-center space-x-2">
            <Home className="w-4 h-4" aria-hidden="true" />
            <span>{state.cta.text}</span>
          </Link>
        )}
      </div>
    </div>
  );
}

export default EmptyState;
